import { ContentObserver } from '@angular/cdk/observers';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { isNonNullish } from '@epicuro-next/utilities';
import { EMPTY, fromEvent, merge, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

/**
 * A directive which observes content for first img or video element and emits aspect ratio
 * Note: This directive won't fire the ratio on video elements, which didn't dispatch loadeddata event
 *  That event is only dispatched on autoplay or if user clicks on play
 */
@Directive({
  selector: '[atxMediaAspectRatio]',
  standalone: false,
})
export class MediaAspectRatioDirective implements OnInit, OnDestroy {
  @Input()
  public videoMultiplier = 1;

  @Input()
  public iframeMultiplier = 1;

  @Input()
  public imageMultiplier = 1;

  @Output()
  public mediaAspectRatio = new EventEmitter<number>();

  constructor(
    private elementRef: ElementRef,
    private observer: ContentObserver,
  ) {}

  private destroy$$ = new Subject<void>();

  public ngOnInit() {
    this.observeAspectRatio();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  private observeAspectRatio() {
    const elm: HTMLDivElement = this.elementRef.nativeElement;
    this.observer
      .observe(elm)
      .pipe(
        startWith(null),
        map(() => ({
          img: elm.querySelector('img'),
          video: elm.querySelector('video'),
          iframe: elm.querySelector('iframe'),
        })),
        map(({ img, video, iframe }) => ({
          img: img
            ? merge(
                fromEvent(img, 'load'),
                fromEvent(img, 'error'),
                of(null),
              ).pipe(map(() => this.imgRatio(img)))
            : undefined,
          video: video
            ? merge(
                fromEvent(video, 'loadeddata'),
                fromEvent(video, 'error'),
                of(null),
              ).pipe(map(() => this.videoRatio(video)))
            : undefined,
          iframe: iframe
            ? merge(
                fromEvent(iframe, 'load'),
                fromEvent(iframe, 'error'),
                of(null),
              ).pipe(map(() => this.iframeRatio(iframe)))
            : undefined,
        })),
        switchMap(({ img, video, iframe }) => video ?? img ?? iframe ?? EMPTY),
        filter(isNonNullish),
        filter((ratio) => !isNaN(ratio)),
        distinctUntilChanged(),
        takeUntil(this.destroy$$),
      )
      .subscribe((ratio) => {
        this.mediaAspectRatio.emit(ratio);
      });
  }

  private imgRatio(img: HTMLImageElement) {
    return (img.naturalWidth / img.naturalHeight) * this.imageMultiplier;
  }

  private videoRatio(video: HTMLVideoElement) {
    return (video.videoWidth / video.videoHeight) * this.videoMultiplier;
  }

  private iframeRatio(iframe: HTMLIFrameElement) {
    return (
      (parseInt(iframe.width, 10) / parseInt(iframe.height, 10)) *
      this.iframeMultiplier
    );
  }
}
